import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiClient, formatApiErrorDetail, setPreviewToken } from "../lib/apiClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [previewInfo, setPreviewInfo] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginWithPassword = async (email, password) => {
    setPreviewToken(null);
    setPreviewInfo(null);
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const loginWithCode = async (email, code) => {
    setPreviewToken(null);
    setPreviewInfo(null);
    try {
      const { data } = await apiClient.post("/auth/code-login", { email, code });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const requestMagicLink = async (email) => {
    try {
      await apiClient.post("/auth/magic-link/request", { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const verifyMagicLink = async (token) => {
    setPreviewToken(null);
    setPreviewInfo(null);
    try {
      const { data } = await apiClient.get(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const previewMagicLink = async (token) => {
    try {
      const { data } = await apiClient.get(`/auth/magic-link/preview?token=${encodeURIComponent(token)}`);
      return { ok: true, identity: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const confirmMagicLink = async (token) => {
    setPreviewToken(null);
    setPreviewInfo(null);
    try {
      const { data } = await apiClient.post("/auth/magic-link/confirm", { token });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    setPreviewToken(null);
    setPreviewInfo(null);
    await apiClient.post("/auth/logout");
    setUser(false);
  };

  const setPassword = async (currentPassword, newPassword) => {
    try {
      await apiClient.post("/auth/set-password", { current_password: currentPassword || null, new_password: newPassword });
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      await apiClient.post("/auth/password-reset/request", { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const confirmPasswordReset = async (token, newPassword) => {
    try {
      await apiClient.post("/auth/password-reset/confirm", { token, new_password: newPassword });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const startPreview = async (tenant, role, userId) => {
    try {
      const { data } = await apiClient.post("/auth/impersonate", { tenant_id: tenant.id, role, user_id: userId });
      setPreviewToken(data.access_token);
      setPreviewInfo({ role, tenantName: tenant.name });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const stopPreview = async () => {
    setPreviewToken(null);
    setPreviewInfo(null);
    await refresh();
  };

  return (
    <AuthContext.Provider value={{ user, previewInfo, loginWithPassword, loginWithCode, requestMagicLink, verifyMagicLink, previewMagicLink, confirmMagicLink, logout, setPassword, requestPasswordReset, confirmPasswordReset, refresh, startPreview, stopPreview }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
